import { searchableAssets } from "@/constants";
import { Asset } from "@/store/slices/types/applicationFormSlice";
import { getAssetFieldAnchorId } from "@/utils/commonFunctions";
import { RequiredFieldRule } from "@/utils/requiredFieldRules";

/** Field names double as the anchor id prefix of the matching input. */
export const ASSET_FIELD = {
  assetValue: "assetValue",
  assetType: "assetType",
  typeOfSale: "typeOfSale",
  condition: "condition",
  make: "make",
  model: "model",
  glassesMake: "glassesMake",
  glassesModel: "glassesModel",
  glassesVariant: "glassesVariant",
  registrationNumber: "registrationNumber",
  registrationState: "registrationState",
  vin: "vin",
} as const;

export interface AssetRequiredFieldSource {
  asset: Asset;
  /**
   * True when asset details are captured through Glass's Guide. Variant, VIN and registration
   * inputs only exist in that mode, so requirements for them are skipped otherwise.
   */
  isGlassesGuideMode: boolean;
  /** True when the registration number and state inputs are rendered and editable. */
  canEnterRegistration?: boolean;
  /** True when the VIN input is rendered and editable. */
  canEnterVin?: boolean;
}

/** Glass's Guide selections are stored as `{ label, value }`; manual entry stores a plain string. */
type AssetFieldValue = string | { label?: string | null } | null | undefined;

const getFieldLabel = (value: AssetFieldValue): string => {
  if (!value) return "";
  return (typeof value === "string" ? value : value.label || "").trim();
};

const hasFieldValue = (asset: Asset, fieldName: keyof Asset) =>
  Boolean(getFieldLabel(asset?.[fieldName] as AssetFieldValue));

/**
 * Takes the asset type and manual flag as values rather than reading them off the stored asset:
 * the form edits an asset type locally and only persists it once the update request resolves, so
 * the caller must pass the same values the inputs are rendered from.
 */
export const isGlassesGuideAssetMode = ({
  assetType,
  isManualAssets,
  isGlassesGuideSearchable,
}: {
  assetType?: string;
  isManualAssets?: boolean;
  isGlassesGuideSearchable?: boolean;
}) => Boolean(isGlassesGuideSearchable) && !isManualAssets && searchableAssets.includes(assetType || "");

/**
 * Make and model are captured by a Glass's Guide dropdown or a manual text input depending
 * on the mode, so the rule checks and anchors to whichever input is on screen.
 */
const createGlassesOrManualRule = ({
  key,
  label,
  glassesFieldName,
  manualFieldName,
}: {
  key: string;
  label: string;
  glassesFieldName: keyof Asset;
  manualFieldName: keyof Asset;
}): RequiredFieldRule<AssetRequiredFieldSource> => ({
  key,
  label,
  getAnchorId: ({ asset, isGlassesGuideMode }) =>
    getAssetFieldAnchorId(isGlassesGuideMode ? glassesFieldName : manualFieldName, asset?._id),
  // Only the input on screen counts. Switching modes leaves the other mode's value behind, and
  // accepting it would report the visibly empty field as filled in.
  isSatisfied: ({ asset, isGlassesGuideMode }) =>
    hasFieldValue(asset, isGlassesGuideMode ? glassesFieldName : manualFieldName),
});

/**
 * Fields an asset needs before an invoice can be requested from the supplier by email.
 * Engine number and registration expiry are intentionally absent: they are only available as
 * read-only Blueflag/NEVDIS lookup data and have no editable field yet.
 */
export const ASSET_INVOICE_REQUIRED_FIELDS: RequiredFieldRule<AssetRequiredFieldSource>[] = [
  {
    key: ASSET_FIELD.condition,
    label: "condition",
    getAnchorId: ({ asset }) => getAssetFieldAnchorId(ASSET_FIELD.condition, asset?._id),
    isSatisfied: ({ asset }) => hasFieldValue(asset, "condition"),
  },
  createGlassesOrManualRule({
    key: ASSET_FIELD.make,
    label: "make",
    glassesFieldName: "glassesMake",
    manualFieldName: "make",
  }),
  createGlassesOrManualRule({
    key: ASSET_FIELD.model,
    label: "model",
    glassesFieldName: "glassesModel",
    manualFieldName: "model",
  }),
  {
    key: ASSET_FIELD.glassesVariant,
    label: "variant",
    getAnchorId: ({ asset }) => getAssetFieldAnchorId(ASSET_FIELD.glassesVariant, asset?._id),
    isApplicable: ({ isGlassesGuideMode }) => isGlassesGuideMode,
    isSatisfied: ({ asset }) => hasFieldValue(asset, "glassesVariant"),
  },
  {
    key: "registrationNumberOrVin",
    label: "VIN, or registration number and state",
    getAnchorId: ({ asset, canEnterRegistration, canEnterVin }) => {
      // Once a registration number is typed the state is the only thing left, so point the user at it.
      if (canEnterRegistration && hasFieldValue(asset, "registrationNumber"))
        return getAssetFieldAnchorId(ASSET_FIELD.registrationState, asset?._id);
      if (canEnterVin) return getAssetFieldAnchorId(ASSET_FIELD.vin, asset?._id);

      return canEnterRegistration ? getAssetFieldAnchorId(ASSET_FIELD.registrationNumber, asset?._id) : "";
    },
    /**
     * VIN and registration are only editable next to the Blueflag lookups, which need the vehicle
     * or PPSR search to be enabled for the org and the user. Without them the fields can never be
     * filled in, so requiring them would disable the request permanently.
     */
    isApplicable: ({ isGlassesGuideMode, canEnterRegistration, canEnterVin }) =>
      isGlassesGuideMode && Boolean(canEnterRegistration || canEnterVin),
    isSatisfied: ({ asset }) =>
      hasFieldValue(asset, "vin") ||
      (hasFieldValue(asset, "registrationNumber") && hasFieldValue(asset, "registrationState")),
  },
];
